import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Receipt, TrendingUp, Printer } from 'lucide-react';
import { api, ErrorApi, traerTodo } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useListaUrl, consultaLista } from '../lib/urlEstado';
import ListaEstandar from '../ui/ListaEstandar';
import Paginador from '../ui/Paginador';
import EstadoVacio from '../ui/EstadoVacio';
import ChipEstado from '../ui/ChipEstado';
import TicketVenta from '../ui/TicketVenta';
import { uuidv7 } from '../lib/uuidv7';
import ModalConfirmar from '../ui/ModalConfirmar';
import TiempoRelativo from '../ui/TiempoRelativo';
import { SelectFiltro, RangoFechas } from '../ui/Filtros';
import Modal from '../ui/Modal';

const ESTADO_VENTA = {
  COMPLETADA: { etiqueta: 'Completada', color: 'verde' },
  CANCELADA: { etiqueta: 'Cancelada', color: 'gris' },
};

const dinero = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    .format(Number(n || 0));

export default function Ventas() {
  const { tieneNivel } = useAuth();
  const [pestana, setPestana] = useState('ventas');
  useSubpantalla('pestana', pestana !== 'ventas', () => setPestana('ventas'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Ventas</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Lo cobrado en mostrador. Una venta no se edita: se cancela, y la
          cancelación devuelve la mercancía al inventario.
        </p>
      </div>

      {tieneNivel('DUENO') && (
        <div className="border-b border-gray-200 flex gap-1 flex-wrap">
          {[['ventas', 'Tickets'], ['rentabilidad', 'Rentabilidad']].map(([id, et]) => (
            <button key={id} type="button" onClick={() => setPestana(id)} aria-current={pestana === id ? 'page' : undefined}
                    className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                      pestana === id
                        ? 'border-[var(--color-marca)] text-[var(--color-marca)]'
                        : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}>
              {et}
            </button>
          ))}
        </div>
      )}

      {pestana === 'ventas' ? <PanelVentas /> : <PanelRentabilidad />}
    </div>
  );
}

function PanelVentas() {
  const { tieneNivel } = useAuth();
  const { filtros, pagina, porPagina, desplazamiento, fijarFiltro, fijarPagina } =
    useListaUrl({ estado: '', desde: '', hasta: '' }, { porPagina: 50 });
  const { estado, desde, hasta } = filtros;

  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [cancelar, setCancelar] = useState(null);
  const [devolver, setDevolver] = useState(null);
  const [detalle, setDetalle] = useState(null);

  const consulta = useCallback(
    (limite, salto) => `/ventas?${consultaLista(filtros, {
      limite, desplazamiento: salto,
    })}`,
    [filtros],
  );

  // La recarga manual (tras cancelar una venta) SÍ puede marcar "cargando"
  // de inmediato: viene de un clic, no del render.
  const cargar = useCallback(() => {
    setCargando(true);
    return api.getConTotal(consulta(porPagina, desplazamiento))
      .then((r) => { setFilas(r.filas); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [consulta, porPagina, desplazamiento]);

  useEffect(() => {
    let vigente = true;
    api.getConTotal(consulta(porPagina, desplazamiento))
      .then((r) => { if (vigente) { setFilas(r.filas); setTotal(r.total); } })
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [consulta, porPagina, desplazamiento]);

  // Un cierre de mes se exporta entero o no sirve. Cada ticket arrastra
  // sus renglones, así que se piden de 200 en 200 y no de 500: la página
  // pesada no es la de la pantalla, es la de la exportación.
  const exportarTodo = useCallback(
    (alAvanzar) => traerTodo(
      (limite, salto) => consulta(limite, salto),
      { porPagina: 200, alAvanzar },
    ),
    [consulta],
  );

  const columnas = [
    { etiqueta: 'Folio', valor: (v) => v.folio, clase: 'font-mono text-xs' },
    {
      etiqueta: 'Cuándo', tipo: 'fecha_hora', valor: (v) => v.fecha,
      render: (v) => <TiempoRelativo valor={v.fecha} />,
    },
    { etiqueta: 'Cajero', valor: (v) => v.cajero || '', clase: 'text-gray-500' },
    {
      etiqueta: 'Artículos', alineado: 'derecha',
      valor: (v) => v.renglones.length,
    },
    {
      etiqueta: 'Descuento', alineado: 'derecha', tipo: 'moneda',
      valor: (v) => Number(v.descuento_total),
      render: (v) => (Number(v.descuento_total) > 0
        ? <span className="text-amber-700 tabular-nums">{dinero(v.descuento_total)}</span>
        : <span className="text-gray-500">—</span>),
    },
    {
      etiqueta: 'Total', alineado: 'derecha', tipo: 'moneda',
      valor: (v) => Number(v.total),
      render: (v) => (
        <span className={`tabular-nums font-semibold ${
          v.estado === 'CANCELADA' ? 'text-gray-500 line-through' : 'text-gray-900'
        }`}>
          {dinero(v.total)}
        </span>
      ),
    },
    { etiqueta: 'Pago', valor: (v) => v.metodo_pago, clase: 'text-gray-500' },
    {
      etiqueta: 'Estado', valor: (v) => v.estado,
      render: (v) => <ChipEstado mapa={ESTADO_VENTA} clave={v.estado} />,
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Tickets"
        subtitulo="Cada venta con su desglose. Los importes cancelados se tachan, nunca desaparecen."
        filtros={
          <>
            <SelectFiltro valor={estado} onChange={(v) => fijarFiltro({ estado: v })} vacio="Todos los estados"
                          opciones={[
                            { valor: 'COMPLETADA', etiqueta: 'Completadas' },
                            { valor: 'CANCELADA', etiqueta: 'Canceladas' },
                          ]} />
            <RangoFechas
              desde={desde}
              hasta={hasta}
              onDesde={(v) => fijarFiltro({ desde: v })}
              onHasta={(v) => fijarFiltro({ hasta: v })}
            />
          </>
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: 'ventas', columnas, obtenerTodo: exportarTodo }}
        paginador={
          <Paginador
            pagina={pagina}
            porPagina={porPagina}
            total={total}
            mostrados={filas.length}
            cargando={cargando}
            onPagina={fijarPagina}
          />
        }
        vacio={
          <EstadoVacio
            icono={Receipt}
            titulo="Todavía no hay ventas"
            descripcion="Las que se cobren en el mostrador aparecerán aquí."
          />
        }
        acciones={(v) => [
          { etiqueta: 'Ver ticket', onClick: () => setDetalle(v) },
          ...(v.estado === 'COMPLETADA' && tieneNivel('ENCARGADO')
            ? [{ etiqueta: 'Devolver', onClick: () => setDevolver(v) },
               { etiqueta: 'Cancelar', onClick: () => setCancelar(v), peligro: true }]
            : []),
        ]}
      />

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {detalle && <ModalTicket venta={detalle} onCerrar={() => setDetalle(null)} />}

      {devolver && (
        <ModalDevolver
          venta={devolver}
          onCerrar={() => setDevolver(null)}
          onListo={() => { setDevolver(null); cargar(); }}
        />
      )}

      {cancelar && (
        <ModalConfirmar
          peligro
          titulo={`Cancelar la venta ${cancelar.folio}`}
          descripcion="La mercancía vuelve al inventario con una devolución. Ni la venta ni su asiento se borran: el par queda visible en el kárdex."
          cambios={[{ campo: 'Estado', antes: 'Completada', despues: 'Cancelada' }]}
          aviso={`Se devolverán ${cancelar.renglones.length} renglón(es) al estante del que salieron.`}
          pedirMotivo
          etiquetaConfirmar="Cancelar venta"
          onConfirmar={async (motivo) => {
            await api.post(`/ventas/${cancelar.id}/cancelar`, { motivo });
            setCancelar(null);
            cargar();
          }}
          onCerrar={() => setCancelar(null)}
        />
      )}
    </>
  );
}

function ModalTicket({ venta, onCerrar }) {
  return (
    <Modal
      titulo={`Venta ${venta.folio}`}
      subtitulo={`${venta.cajero} · ${venta.sucursal || 'Sin sucursal'}`}
      onCerrar={onCerrar}
      derecha={<ChipEstado mapa={ESTADO_VENTA} clave={venta.estado} />}
    >
      <div className="space-y-3">

        {/* LOOP MÓVIL F4 · M1/M3: la tabla se desplaza dentro de su
            propio contenedor, no la página, cuando no cabe en el teléfono. */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm border-y border-gray-200">
          <tbody>
            {venta.renglones.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="py-1.5">
                  <div className="text-gray-800">{r.producto}</div>
                  <div className="text-xs font-mono text-gray-500">
                    {r.sku} · {Number(r.cantidad)} × {dinero(r.precio_unitario)}
                    {r.ubicacion ? ` · ${r.ubicacion}` : ''}
                    {/* De qué lote salió. Es la respuesta a «¿a quién le
                        vendimos el lote retirado?», que sin esto obligaba a
                        retirar todo. El dato existía en la tabla desde el
                        día uno y no lo escribía nadie. */}
                    {r.codigo_lote && (
                      <span className="ml-1 text-[var(--color-marca)]">lote {r.codigo_lote}</span>
                    )}
                  </div>
                </td>
                <td className="py-1.5 text-right tabular-nums whitespace-nowrap">
                  {dinero(r.importe)}
                  {r.margen != null && (
                    <div className="text-xs text-green-700">margen {dinero(r.margen)}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {venta.descuentos.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
            <div className="font-semibold text-amber-900">Descuentos aplicados</div>
            {venta.descuentos.map((d, i) => (
              <div key={i} className="text-amber-800">
                {d.sku ? `${d.sku}: ` : 'Al total: '}
                {d.tipo === 'PORCENTAJE' ? `${Number(d.valor)}%` : dinero(d.valor)}
                {' → '}{dinero(d.monto_resultante)}
                <div className="text-xs">
                  {d.motivo_tipo}{d.motivo ? ` — ${d.motivo}` : ''} · aplicó {d.aplicado_por}
                  {d.autorizado_por ? `, autorizó ${d.autorizado_por}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-sm space-y-1">
          <Fila etiqueta="Subtotal" valor={dinero(venta.subtotal)} />
          {Number(venta.descuento_total) > 0 && (
            <Fila etiqueta="Descuentos" valor={`−${dinero(venta.descuento_total)}`} />
          )}
          <Fila etiqueta="IVA" valor={dinero(venta.impuestos)} />
          <div className="flex justify-between pt-1 border-t border-gray-200">
            <span className="font-semibold">Total</span>
            <span className="font-semibold tabular-nums text-lg">{dinero(venta.total)}</span>
          </div>
          {venta.margen != null && (
            <Fila etiqueta="Margen de la venta" valor={dinero(venta.margen)} />
          )}
        </div>

        {venta.estado === 'CANCELADA' && (
          <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-700">
            Cancelada: {venta.motivo_cancelacion}
          </div>
        )}

        {/* EL TICKET NO SE VE AQUI, SE IMPRIME. Esta pantalla es la
            vista de auditoria —enseña margenes, lotes y quien autorizo
            cada descuento— y esos son justo los datos que no pueden
            salir por el mostrador. El papel va fuera de la pantalla y
            solo aparece al imprimir. */}
        <div aria-hidden="true"
             style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <TicketVenta venta={venta} copia />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => window.print()}
                  className="inline-flex items-center justify-center gap-1.5 border
                             border-gray-300 text-gray-700 font-semibold py-2 rounded-lg
                             hover:bg-gray-50 focus:outline-none focus:ring-2
                             focus:ring-[var(--color-marca)]">
            <Printer className="h-4 w-4" aria-hidden="true" /> Reimprimir
          </button>
          <button type="button" onClick={onCerrar}
                  className="border border-gray-300 text-gray-700 font-semibold py-2 rounded-lg hover:bg-gray-50">
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Devolver PARTE de una venta.
//
// SE PIDE LA FICHA COMPLETA AL ABRIR. La lista trae la venta, pero lo que
// decide cuanto se puede devolver es lo que YA se devolvio, y eso cambia
// cada vez que alguien devuelve algo. Trabajar con la copia de la lista
// aceptaria una cantidad que el servidor va a rechazar — y el cliente ya
// esta enfrente.
function ModalDevolver({ venta, onCerrar, onListo }) {
  const [ficha, setFicha] = useState(null);
  const [cant, setCant] = useState({});
  const [motivoTipo, setMotivoTipo] = useState('PRODUCTO_DEFECTUOSO');
  const [motivo, setMotivo] = useState('');
  // Una clave por apertura del dialogo: el doble clic y el reintento
  // comparten clave, y un reembolso aplicado dos veces es dinero.
  const claveRef = useRef(null);
  if (claveRef.current == null) { claveRef.current = uuidv7(); }

  useEffect(() => {
    let vivo = true;
    api.get(`/ventas/${venta.id}`)
      .then((v) => vivo && setFicha(v))
      .catch(() => vivo && setFicha(venta));
    return () => { vivo = false; };
  }, [venta]);

  const renglones = (ficha?.renglones || []).map((r) => ({
    ...r,
    queda: Number(r.cantidad) - Number(r.cantidad_devuelta || 0),
  }));
  const totalDevolver = renglones.reduce((s, r) => {
    const n = Number(cant[r.id] || 0);
    return s + (n > 0 ? (Number(r.importe) * n) / Number(r.cantidad) : 0);
  }, 0);

  return (
    <ModalConfirmar
      titulo={`Devolver de la venta ${venta.folio}`}
      descripcion="La venta no se toca: se anota lo que vuelve y el dinero sale por donde entró. El ticket original sigue diciendo lo que se cobró ese día."
      extra={
        <div className="space-y-3">
          {!ficha && <p className="text-sm text-gray-500">Cargando la venta…</p>}

          {renglones.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-gray-800 truncate">{r.producto}</div>
                <div className="text-xs font-mono text-gray-500">
                  {r.sku} · {dinero(r.precio_unitario)} c/u
                  {/* Lo ya devuelto se DICE: sin esto, quien teclea 3 sobre
                      un renglón del que ya volvieron 4 de 5 se lleva un
                      rechazo con el cliente delante. */}
                  {Number(r.cantidad_devuelta) > 0 && (
                    <span className="ml-1 text-amber-700">
                      ya volvieron {Number(r.cantidad_devuelta)} de {Number(r.cantidad)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <input
                  type="number" min="0" max={r.queda} step="any"
                  value={cant[r.id] ?? ''}
                  aria-label={`Cantidad a devolver de ${r.sku}`}
                  onChange={(e) => setCant((c) => ({ ...c, [r.id]: e.target.value }))}
                  disabled={r.queda <= 0}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-right tabular-nums disabled:bg-gray-100"
                />
                <div className="text-xs text-gray-500 mt-0.5 tabular-nums">
                  {r.queda > 0 ? `quedan ${r.queda}` : 'nada por devolver'}
                </div>
              </div>
            </div>
          ))}

          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              ¿Por qué vuelve?
            </span>
            <select value={motivoTipo} onChange={(e) => setMotivoTipo(e.target.value)}
                    className="entrada">
              {MOTIVOS_DEVOLUCION.map(([v, et]) => (
                <option key={v} value={v}>{et}</option>
              ))}
            </select>
          </label>

          {motivoTipo === 'OTRO' && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Explica cuál
              </span>
              <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     className="entrada" />
            </label>
          )}

          {/* Cuánto sale del cajón, ANTES de confirmarlo. Se calcula a
              prorrata de lo cobrado, no del precio de lista: con descuento
              no es lo mismo. */}
          <div className="flex justify-between border-t border-gray-200 pt-2">
            <span className="font-semibold text-gray-900">Se devuelven</span>
            <span className="font-semibold tabular-nums text-lg">
              {dinero(totalDevolver)}
            </span>
          </div>
          {ficha && (
            <p className="text-xs text-gray-500">
              Por {ficha.metodo_pago === 'EFECTIVO' ? 'efectivo, del cajón' : ficha.metodo_pago.toLowerCase()}:
              el reembolso va por donde vino el dinero.
            </p>
          )}
        </div>
      }
      etiquetaConfirmar="Registrar la devolución"
      onConfirmar={async () => {
        const lineas = renglones
          .map((r) => ({ id_renglon: r.id, cantidad: Number(cant[r.id] || 0) }))
          .filter((l) => l.cantidad > 0);
        if (!lineas.length) throw new ErrorApi('Indica qué vuelve y cuánto.');
        await api.post(`/ventas/${venta.id}/devolver`, {
          renglones: lineas,
          motivo_tipo: motivoTipo,
          ...(motivo ? { motivo } : {}),
          clave_idempotencia: claveRef.current,
        });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

const MOTIVOS_DEVOLUCION = [
  ['PRODUCTO_DEFECTUOSO', 'Venía defectuoso'],
  ['NO_ERA_LO_QUE_QUERIA', 'No era lo que quería'],
  ['ERROR_DEL_MOSTRADOR', 'Error del mostrador'],
  ['GARANTIA', 'Garantía'],
  ['OTRO', 'Otro'],
];

function Fila({ etiqueta, valor }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{etiqueta}</span>
      <span className="tabular-nums text-gray-800">{valor}</span>
    </div>
  );
}

// Solo DUENO llega aquí: la pestaña no se pinta para los demás y el
// backend responde 403 igual.
function PanelRentabilidad() {
  const [por, setPor] = useState('producto');
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    api.get(`/ventas/informes/rentabilidad?por=${por}`)
      .then((d) => vigente && setFilas(d))
      .catch(() => vigente && setFilas([]))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [por]);

  const columnas = [
    { etiqueta: por === 'cajero' ? 'Cajero' : 'Concepto', valor: (f) => f.nombre,
      clase: 'font-medium text-gray-900' },
    { etiqueta: 'Unidades', alineado: 'derecha', valor: (f) => Number(f.unidades) },
    { etiqueta: 'Ingreso', alineado: 'derecha', tipo: 'moneda', valor: (f) => Number(f.ingreso),
      render: (f) => <span className="tabular-nums">{dinero(f.ingreso)}</span> },
    { etiqueta: 'Costo', alineado: 'derecha', tipo: 'moneda', valor: (f) => Number(f.costo),
      render: (f) => <span className="tabular-nums text-gray-500">{dinero(f.costo)}</span> },
    {
      etiqueta: 'Margen', alineado: 'derecha', tipo: 'moneda', valor: (f) => Number(f.margen),
      render: (f) => (
        <span className={`tabular-nums font-semibold ${
          Number(f.margen) < 0 ? 'text-red-700' : 'text-green-700'
        }`}>
          {dinero(f.margen)}
        </span>
      ),
    },
    {
      etiqueta: '%', alineado: 'derecha', valor: (f) => Number(f.margen_pct),
      render: (f) => (
        <span className={`tabular-nums ${
          Number(f.margen_pct) < 0 ? 'text-red-700' : 'text-gray-600'
        }`}>
          {Number(f.margen_pct).toFixed(1)}%
        </span>
      ),
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Rentabilidad"
        subtitulo="Con el costo congelado en cada venta, no con el promedio de hoy: la cifra de marzo sigue siendo la de marzo."
        filtros={
          <SelectFiltro valor={por} onChange={setPor}
                        opciones={[
                          { valor: 'producto', etiqueta: 'Por producto' },
                          { valor: 'categoria', etiqueta: 'Por categoría' },
                          { valor: 'cajero', etiqueta: 'Por cajero' },
                        ]} />
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: `rentabilidad-${por}`, columnas }}
        vacio={
          <EstadoVacio
            icono={TrendingUp}
            titulo="Sin ventas que analizar"
            descripcion="El margen aparece en cuanto se cobre la primera venta."
          />
        }
      />
      <p className="mt-3 text-xs text-gray-500 max-w-2xl leading-relaxed">
        Un margen negativo significa que se vendió por debajo del costo con el
        que entró la mercancía. Revisa el precio capturado y los descuentos
        aplicados.
      </p>
    </>
  );
}
